// src/components/ExcelImportExport.jsx
import React, { useRef } from "react";
import { useToast } from "../../contexts/ToastContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faUpload } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import "./styles/ExcelImportExport.css";

const ExcelImportExport = ({
  lists,
  filteredLists,
  sdataGroup,
  onListUpdate,
  fileNamePrefix = "All_Lists",
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const getResponseItems = (questionNumber) => {
    if (!questionNumber || !sdataGroup) return [];

    // Try multiple strategies to find the question
    let question = Object.values(sdataGroup).find(q => q?.Question_number === questionNumber) ||
                   sdataGroup[questionNumber] ||
                   sdataGroup[Object.keys(sdataGroup).find(k => k.toLowerCase() === questionNumber.toLowerCase())] ||
                   Object.values(sdataGroup).find(q => 
                     q && ['questionNumber', 'question', 'id', 'number'].some(prop => q[prop] === questionNumber)
                   );

    if (!question || !Array.isArray(question.rows)) 
    {
        if (!question || !Array.isArray(question.values)) return [];
        return question.values.map(item => item);  
    }
      
    return question.rows.map(item => item);
  };

  // Helper function to build filter logic
  const buildFilterLogic = (questionNumber1, indices1, questionNumber2, indices2) => {
    let logic = "";
    
    if (questionNumber1 && indices1.length > 0 && questionNumber2 && indices2.length > 0) {
      // Both questions present - create AND combinations
      const conditions = [];
      indices1.forEach(idx1 => {
        indices2.forEach(idx2 => {
          conditions.push(`(${questionNumber1}=${idx1} and ${questionNumber2}=${idx2})`);
        });
      });
      logic = conditions.join(" or ");
    } else if (questionNumber1 && indices1.length > 0) {
      // Only first question present
      if (indices1.length === 1) {
        logic = `${questionNumber1}=${indices1[0]}`;
      } else {
        logic = `${questionNumber1}=${indices1.join(",")}`;
      }
    } else if (questionNumber2 && indices2.length > 0) {
      // Only second question present
      if (indices2.length === 1) {
        logic = `${questionNumber2}=${indices2[0]}`;
      } else {
        logic = `${questionNumber2}=${indices2.join(",")}`;
      }
    }
    
    return logic;
  };

  const generateSheetData = (list) => {
    const listKey = Object.keys(list)[0];
    const listItems = list[listKey] || [];
    const dep1 = list.dependent1;
    const dep2 = list.dependent2;

    const usedQuestions = (list.questionNumbers || [])
      .map((q) => (typeof q === "object" ? q.name : q))
      .filter(Boolean)
      .join(", ");

    const dep1Items = dep1 ? getResponseItems(dep1) : [];
    const dep2Items = dep2 ? getResponseItems(dep2) : [];

    let headers = [""];
    let hindex = [""];
    let subHeaders = [""];
    let subindex = [""];
    
    if (dep1 && dep2) {
      headers = [dep1];
      hindex = ["Index"];
      subHeaders = [dep2];
      subindex = ["Index"];
      dep1Items.forEach((text1) => {
        dep2Items.forEach((text2) => {
          headers.push(text1.text || text1.title || text1.label || "Unknown");
          hindex.push(text1.index);
          subHeaders.push(text2.text || text2.title || text2.label || "Unknown");
          subindex.push(text2.index);
        });
      });
    } else if (dep1) {
      headers = [dep1];
      hindex = ["Index"];
      dep1Items.forEach((text) =>
        { headers.push(text.text || text.title || text.label || "Unknown");
          hindex.push(text.index);
        });
    } else {
      headers.push("Item");
    }

    const rows = listItems.map((item) => {
      const rowText = item.text || item.title || "No text";
      const row = [rowText];
      for (let i = 1; i < headers.length; i++) row.push("");
      return row;
    });

    const sheetData = [];
    if (usedQuestions) {
      sheetData.push([`Questions: ${usedQuestions}`, ...Array(headers.length - 1).fill("")]);
    }
    sheetData.push(headers);
    sheetData.push(hindex);
    if (subHeaders.length > 1) 
      {
        sheetData.push(subHeaders);
        sheetData.push(subindex);
      }
    sheetData.push(...rows);

    return sheetData;
  };

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    let index = 0;
    filteredLists.forEach((list) => {
      const listKey = Object.keys(list)[0];
      const safeName = listKey.replace(/[*?:\/\\[\]]/g, "_").slice(0, 31);
      const ws = XLSX.utils.aoa_to_sheet(generateSheetData(list));
      XLSX.utils.book_append_sheet(workbook, ws, 'Sheet' + (index++));
    });

    const fileName = `${fileNamePrefix}_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const handleFileUpload = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (event) => {
    const data = new Uint8Array(event.target.result);
    const workbook = XLSX.read(data, { type: "array" });

    const updatedLists = [...lists];

    const updatedSdataGroup = Array.isArray(sdataGroup)
      ? [...sdataGroup]
      : { ...sdataGroup };

    workbook.SheetNames.forEach((sheetName) => {
      const ws = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });

      if (json.length < 2 || json[2][0]!="Index") return;

      const questionsRow = json.find(
        (row) => typeof row[0] === "string" && row[0].startsWith("Questions:")
      );
      if (!questionsRow) return;

      const questionNumbersFromExcel = questionsRow[0]
        .replace("Questions:", "")
        .split(",")
        .map(q => q.trim())
        .filter(Boolean);

      const matchingList = lists.find((list) => {
        const listQs = (list.questionNumbers || [])
          .map(q => (typeof q === "object" ? q.name : q))
          .filter(Boolean);

        return (
          listQs.length === questionNumbersFromExcel.length &&
          listQs.every(q => questionNumbersFromExcel.includes(q))
        );
      });

      if (!matchingList) return;

      const listKey = Object.keys(matchingList)[0];
      const listItems = matchingList[listKey] || [];

      const questionsRowIndex = json.indexOf(questionsRow);
      const headerRowIndex = questionsRowIndex + 1;
      const indexRowIndex = questionsRowIndex + 2;

      let subHeaderRowIndex = -1;
      let subIndexRowIndex = -1;
      let dataStartRow = questionsRowIndex + 3;
    // Determine which questions correspond to which headers
      let question1 = json[1][0];
      let question2 =  null;

      if (json[indexRowIndex] && json[indexRowIndex+2][0] === "Index") {
        question2 = json[3][0];
        subHeaderRowIndex = indexRowIndex+1;
        subIndexRowIndex = indexRowIndex + 2;
        dataStartRow = subIndexRowIndex + 1;
      }

      const headers = json[headerRowIndex] || [];
      const indices = json[indexRowIndex] || [];
      const subHeaders = subHeaderRowIndex >= 0 ? json[subHeaderRowIndex] || [] : [];
      const subIndices = subIndexRowIndex >= 0 ? json[subIndexRowIndex] || [] : [];


      const newItems = listItems.map((item, rowIdx) => {
        const excelRow = json[dataStartRow + rowIdx];
        if (!excelRow) return item;

        const rowText = excelRow[0] || "";
        if (
          item.text !== rowText &&
          item.title !== rowText &&
          item.label !== rowText
        ) {
          return item;
        }

        const q1Set = new Set();
        const q2Set = new Set();
        const andPairs = [];

        for (let colIdx = 1; colIdx < excelRow.length; colIdx++) {
          if (excelRow[colIdx] === "x" || excelRow[colIdx] === "X") {
            const i1 = indices[colIdx];
            const i2 = subIndices[colIdx];

            if (i1 !== undefined && i1 !== "") q1Set.add(i1.toString());
            if (i2 !== undefined && i2 !== "") q2Set.add(i2.toString());

            if (
              i1 !== undefined &&
              i2 !== undefined &&
              i1 !== "" &&
              i2 !== ""
            ) {
              andPairs.push({
                q1: i1.toString(),
                q2: i2.toString()
              });
            }
          }
        }

        let filterLogic = "";

        // ✅ FIXED CASE (your problem)
        if (andPairs.length > 0 && question1 && question2) {
          filterLogic = andPairs
            .map(p => `(${question1}=${p.q1} and ${question2}=${p.q2})`)
            .join(" or ");
        }
        // fallback (original behavior)
        else if (q1Set.size > 0 && q2Set.size > 0 && question1 && question2) {
          filterLogic =
            `${question1}=${Array.from(q1Set).join(",")}` +
            ` and ${question2}=${Array.from(q2Set).join(",")}`;
        }
        else if (q1Set.size > 0 && question1) {
          filterLogic = `${question1}=${Array.from(q1Set).join(",")}`;
        }
        else if (q2Set.size > 0 && question2) {
          filterLogic = `${question2}=${Array.from(q2Set).join(",")}`;
        }

        if (!filterLogic) return item;

        if (matchingList.questionNumbers) {
          matchingList.questionNumbers.forEach((qInfo) => {
            const { name: qName, type: qType } = qInfo;

            if (!Array.isArray(sdataGroup) && sdataGroup[qName]) {
              const prop =
                qType === "rows" ? "Rows" :
                qType === "cols" ? "Cols" :
                qType === "statements" ? "Statements" :
                "values";

              const qObj = updatedSdataGroup[qName];
              if (!qObj || !Array.isArray(qObj[prop])) return;

              const idx = qObj[prop].findIndex(
                r => r.text === rowText || r.title === rowText || r.label === rowText
              );

              if (idx !== -1) {
                updatedSdataGroup[qName][prop][idx] = {
                  ...updatedSdataGroup[qName][prop][idx],
                  filterLogic
                };
              }
            }

            if (Array.isArray(Object.values(sdataGroup))) {
              const prop =
                qType === "rows" ? "rows" :
                qType === "cols" ? "columns" :
                qType === "statements" ? "statements" :
                "values";

              const qObj = Object.values(sdataGroup).find(item => item.Question_number === qName);
              if (!qObj || !Array.isArray(qObj[prop])) return;
            const entry = Object.entries(updatedSdataGroup).find(([key, val]) => 
              val.Question_number === qName
            );

              const idx = qObj[prop].findIndex(
                r => r.text === rowText || r.title === rowText || r.label === rowText
              );

              if (entry && idx !== -1) {
                const [actualKey, itemData] = entry;
                if (updatedSdataGroup[actualKey][prop] && updatedSdataGroup[actualKey][prop][idx]) {
              updatedSdataGroup[actualKey][prop][idx] = {
      ...updatedSdataGroup[actualKey][prop][idx],
      filterLogic
    };
              }
              }

            }
          });
        }

        return { ...item, filterLogic };
      });

      const listIndex = updatedLists.findIndex(l => Object.keys(l)[0] === listKey);
      if (listIndex !== -1) {
        updatedLists[listIndex] = { ...matchingList, [listKey]: newItems };
      }
    });

    updatedLists.forEach(list => onListUpdate?.(list));

    if (Array.isArray(sdataGroup)) {
      window.setSdatagroup?.(updatedSdataGroup);
    } else {
      window.setDataGroup?.(updatedSdataGroup);
    }

    toast.success("Excel imported successfully. Filter logic updated.");
  };

  reader.readAsArrayBuffer(file);
  if (fileInputRef.current) fileInputRef.current.value = "";
};

  return (
    <div className="header-actions">
      <label className="upload-btn" title="Upload Excel to import filter logic">
        <FontAwesomeIcon icon={faUpload} />
        Upload Excel
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
      </label>

      <button className="download-btn" onClick={downloadExcel}>
        <FontAwesomeIcon icon={faDownload} />
        Download Excel
      </button>
    </div>
  );
};

export default ExcelImportExport;